<template>
    <div v-if="!hasAnswer">
        <div
            class="survey-receive-options"
            :class="{
                nps: isNps,
                fivestar: isFiveStar,
            }"
        >
            <button
                v-for="option in options"
                :key="option.key"
                class="survey-receive-options--item"
                :class="{ selected: selectedOptionKeys.includes(option.key) }"
                type="button"
                @click="handleSelectOption(option, $event)"
            >
                {{ option.value }}
            </button>
            <button
                v-if="isMultiSelect"
                class="survey-receive-options--done"
                :class="{ disabled: selectedOptionKeys.length === 0 }"
                :disabled="selectedOptionKeys.length === 0"
                type="button"
                @click="handleDone"
            >
                Done
            </button>
        </div>
        <div
            v-if="isNps || isFiveStar"
            class="survey-receive-options--legend-label"
        >
            <div>
                {{ getLabelLeft() }}
            </div>
            <div>
                {{ getLabelRight() }}
            </div>
        </div>
    </div>
    <div v-else class="survey-receive-options--answer">
        <span>{{ selectedAnswer }}</span>
    </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import type { Ref } from 'vue'
import {
    LABEL_LEFT_NPS_DEFAULT,
    LABEL_LEFT_FIVESTAR_DEFAULT,
    LABEL_RIGHT_NPS_DEFAULT,
    LABEL_RIGHT_FIVESTAR_DEFAULT,
    ReceiveOption,
    SurveyDisplayValues,
    SurveyParams,
    OptionLabels,
} from '@/entities/conversation'
import { shuffleArray } from '@/utils/array'
import { computed } from 'vue'

interface Props {
    item: ReceiveOption
    surveyDisplayValues?: SurveyDisplayValues
    surveyParams: SurveyParams
    optionLabels: OptionLabels | null
}

const props = defineProps<Props>()
const emit = defineEmits(['callNext'])

const options = ref()
const hasAnswer = ref(false)
const selectedAnswer = ref('')
const selectedOptionKeys: Ref<string[]> = ref([])

const isMultiSelect = computed(() => props.item.multiSelect)
const isRandomOptions = computed(() => props.item.randomizeOrder)
const isNps = computed(() => {
    if (props.item.param && !!props.item.param.match(/_nps$/)) {
        return true
    }
    if (props.item.cardType && props.item.cardType == 'nps') {
        return true
    }
    return false
})
const isFiveStar = computed(() => {
    if (props.item.param && !!props.item.param.match(/_fivestar$/)) {
        return true
    }
    if (props.item.cardType && props.item.cardType == 'fivestar') {
        return true
    }
    return false
})

onMounted(() => {
    if (isRandomOptions.value) {
        const randomOptions = shuffleArray(props.item.options)
        options.value = randomOptions
    } else {
        options.value = props.item.options
    }

    if (props.surveyDisplayValues?.[props.item?.param]) {
        displaySavedValues(props.surveyDisplayValues[props.item?.param])
    }
})

const displaySavedValues = (selectedOptionsKeys) => {
    const selectedOptionsKeysArray = selectedOptionsKeys.split('|')
    const selectedOptionKeys = props.item.options.filter((option) =>
        selectedOptionsKeysArray.includes(option.key)
    )

    const selectedOptionValues = selectedOptionKeys.map(
        (option) => option.value
    )

    hasAnswer.value = true
    selectedAnswer.value = selectedOptionValues.join(', ')
}

const handleSelectOption = (option, evt: MouseEvent) => {
    evt.preventDefault()
    if (isMultiSelect.value) {
        const selectedKey = option.key as string

        if (selectedOptionKeys.value.includes(selectedKey)) {
            selectedOptionKeys.value = selectedOptionKeys.value.filter(
                (option) => option !== selectedKey
            )
        } else {
            selectedOptionKeys.value.push(selectedKey)
        }
    } else {
        selectedAnswer.value = option.value
        hasAnswer.value = true
        const newValue = { [props.item.param]: option.key }
        emit('callNext', newValue, selectedAnswer.value)
    }
}

const handleDone = (evt: MouseEvent) => {
    evt.preventDefault()
    if (selectedOptionKeys.value.length > 0) {
        const selectedKeys = selectedOptionKeys.value.join('|')
        displaySavedValues(selectedKeys)
        const newValue = { [props.item.param]: selectedKeys }
        emit('callNext', newValue, selectedAnswer.value)
    }
}

const getLabelLeft = () => {
    if (isNps.value) {
        if (props.optionLabels?.labelLeft) {
            return props.optionLabels?.labelLeft
        }

        if (props.item.optionLabels?.labelLeft) {
            return props.item.optionLabels?.labelLeft
        }
        if (props.surveyParams.labelLeft) {
            return props.surveyParams.labelLeft
        }

        return LABEL_LEFT_NPS_DEFAULT
    }

    if (isFiveStar.value) {
        if (props.surveyParams.labelLeft) {
            return props.surveyParams.labelLeft
        }

        return LABEL_LEFT_FIVESTAR_DEFAULT
    }

    return null
}

const getLabelRight = () => {
    if (isNps.value) {
        if (props.optionLabels?.labelRight) {
            return props.optionLabels?.labelRight
        }
        if (props.item.optionLabels?.labelRight) {
            return props.item.optionLabels?.labelRight
        }
        if (props.surveyParams.labelRight) {
            return props.surveyParams.labelRight
        }

        return LABEL_RIGHT_NPS_DEFAULT
    }

    if (isFiveStar.value) {
        if (props.surveyParams.labelRight) {
            return props.surveyParams.labelRight
        }

        return LABEL_RIGHT_FIVESTAR_DEFAULT
    }

    return null
}
</script>

<style lang="less">
@import '~@/styles/rain/colour.less';

.survey-receive-options {
    display: flex;
    flex-direction: column;
    align-items: center;

    &--item {
        color: var(--brandColor);
        background-color: #fff;
        border: 1px solid var(--brandColor);
        font-size: 1em;
        margin-bottom: 10px;
        padding: 12px 20px;
        text-align: center;
        width: 280px;
        cursor: pointer;
        transition: 0.25s;
        opacity: 1;

        &:hover {
            color: #fff;
            background-color: var(--brandColor);
            opacity: 0.7;
        }

        &.selected {
            color: #fff;
            background-color: var(--brandColor);
        }
    }

    &--done {
        color: var(--brandColor);
        background: @secondaryLightBlue;
        border: none;
        border-radius: 4px;
        font-size: 1em;
        margin-bottom: 10px;
        padding: 12px 20px;
        text-align: center;
        width: 280px;
        cursor: pointer;
        font-weight: bold;
        transition: 0.25s;

        &.disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        &:hover {
            opacity: 0.8;
        }
    }

    &--answer {
        font-family: Roboto, Arial, Helvetica, sans-serif;
        background-color: #eee;
        border-radius: 20px 0px 20px 20px;
        padding: 12px 20px;
        margin-left: 100px;
        align-self: flex-end;
        width: fit-content;
    }

    &.nps {
        display: block;
        text-align: center;
        width: 320px;
        margin: 0px auto;

        .survey-receive-options--item {
            background: var(--brandColor);
            width: 44px;
            height: 44px;
            line-height: 44px;
            color: #fff;
            font-weight: bold;
            font-size: 1.125em;
            border-radius: 100%;
            text-align: center;
            display: inline-block;
            padding: 0px;
            margin: 5px 2px;
        }
    }

    &.fivestar {
        display: flex;
        flex-direction: row;
        justify-content: center;

        .survey-receive-options--item {
            font-weight: normal;
            border: 0;
            margin: 0;
            padding: 0;
            width: 60px;

            &:hover {
                font-weight: bold;
                background-color: transparent;
                color: var(--brandColor);

                &:before {
                    content: '★';
                }
            }

            &:before {
                content: '☆';
                display: block;
                font-size: 50px;
                line-height: 60px;
            }
        }
    }

    &--legend-label {
        display: flex;
        justify-content: space-around;
        margin-top: 0.5rem;
        width: 100%;
        font-size: 11px;
        font-family: 'Roboto', Helvetica, Arial, 'Lucida Grande', sans-serif;
        color: #c1c1c1;
    }
}
</style>
